import React, { useCallback, useMemo, useState } from 'react';
import { Box, Divider, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import { Cancel, ClearAll, Edit, Save } from '@mui/icons-material';
import { FormSectionHeaderProps } from './FormSection.types';

const EDITOR_STYLES = { display: 'flex', alignItems: 'center', gap: 1, mb: 1 };

export const FormSectionHeader = React.memo(function FormSectionHeader({
  section,
  isMobile,
  isAdminMode,
  onSectionTitleChange,
  onClear,
}: FormSectionHeaderProps) {
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [tempTitle, setTempTitle] = useState(section.title);
  const handleStartEditing = useCallback(() => {
    setTempTitle(section.title);
    setIsEditingTitle(true);
  }, [section.title]);
  const handleSaveTitle = useCallback(() => {
    if (
      onSectionTitleChange &&
      section.id &&
      tempTitle.trim() &&
      tempTitle.trim() !== section.title
    ) {
      onSectionTitleChange(section.id, tempTitle.trim());
    }
    setIsEditingTitle(false);
  }, [onSectionTitleChange, section.id, section.title, tempTitle]);
  const handleCancelEditing = useCallback(() => {
    setTempTitle(section.title);
    setIsEditingTitle(false);
  }, [section.title]);
  const handleTitleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setTempTitle(event.target.value);
  }, []);
  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter') handleSaveTitle();
      else if (event.key === 'Escape') handleCancelEditing();
    },
    [handleSaveTitle, handleCancelEditing],
  );
  const styles = useMemo(
    () => ({
      root: { mb: isMobile ? 1 : 2 },
      input: { '& .MuiOutlinedInput-root': { fontSize: isMobile ? '0.95rem' : '1.1rem' } },
      heading: { ...EDITOR_STYLES, mb: isMobile ? 1 : 2 },
      title: {
        fontWeight: 600,
        color: 'text.primary',
        flex: 1,
        fontSize: isMobile ? '1rem' : '1.25rem',
      },
      divider: { mb: isMobile ? 1.5 : 2 },
    }),
    [isMobile],
  );

  return (
    <Box sx={styles.root}>
      {isEditingTitle ? (
        <Box sx={EDITOR_STYLES}>
          <TextField
            value={tempTitle}
            onChange={handleTitleChange}
            onKeyDown={handleKeyPress}
            size="small"
            fullWidth
            autoFocus
            variant="outlined"
            sx={styles.input}
          />
          <Tooltip title="Сохранить">
            <IconButton onClick={handleSaveTitle} size="small" color="primary">
              <Save />
            </IconButton>
          </Tooltip>
          <Tooltip title="Отменить">
            <IconButton onClick={handleCancelEditing} size="small" color="secondary">
              <Cancel />
            </IconButton>
          </Tooltip>
        </Box>
      ) : (
        <Box sx={styles.heading}>
          <Typography variant={isMobile ? 'h6' : 'h5'} component="h2" sx={styles.title}>
            {section.title}
          </Typography>
          {isAdminMode && (
            <Tooltip title="Редактировать название секции">
              <IconButton onClick={handleStartEditing} size="small" color="primary">
                <Edit />
              </IconButton>
            </Tooltip>
          )}
          {onClear && (
            <Tooltip title="Очистить раздел">
              <IconButton onClick={onClear} size="small" color="warning">
                <ClearAll />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}
      <Divider sx={styles.divider} />
    </Box>
  );
});
